import { round } from '@deltasierra/utilities-math';
import { CurrencyCode, CurrencyFloat, CurrencyNumber, getReturnOnInvestmentPercent, getMoment } from '../../common';
import { LostImpressionPercentage } from '../../external';
const moment = getMoment();

export interface RecommendationsMetrics {
    currencyCode: CurrencyCode | null;
    cost: CurrencyNumber;
    impressions: number;
    impressionShareLostByBudget: LostImpressionPercentage;
    totalConversionValue: CurrencyNumber;
}

export interface RecommendationsLocationStats extends RecommendationsMetrics {
    locationId: number;
}

export function lostImpressionShareToMultiplier(lostImpressionShare: LostImpressionPercentage): number {
    return lostImpressionShare.value / (1 - lostImpressionShare.value);
}

export function calculateLostImpressions(
    actualImpressions: number,
    lostImpressionShare: LostImpressionPercentage,
): number {
    const multiplier = lostImpressionShareToMultiplier(lostImpressionShare);
    return round(actualImpressions * multiplier, 0);
}

export function calculateLostImpressionShare(actualImpressions: number, lostImpressions: number): number {
    const potentialImpressions = actualImpressions + lostImpressions;
    const result = lostImpressions / potentialImpressions;
    return result;
}

export function calculateRecommendedSpend(
    actualSpend: CurrencyFloat,
    lostImpressionShareByBudget: LostImpressionPercentage | null,
): CurrencyFloat {
    if (!lostImpressionShareByBudget || lostImpressionShareByBudget.value === 0) {
        return actualSpend;
    }

    const multiplier = lostImpressionShareToMultiplier(lostImpressionShareByBudget);

    let result = multiplier * actualSpend + actualSpend;

    // Round UP to nearest cent
    result = round(Math.ceil(result * 100) / 100, 2);

    return result;
}

export function calculatePotentialReturnOnInvestment(
    actualSpend: CurrencyFloat,
    totalConversionValue: CurrencyFloat,
    lostImpressionShare: LostImpressionPercentage,
    potentialSpend: CurrencyFloat,
): CurrencyFloat {
    /*
     * Cap potential spend at recommended spend level since we can't simply
     * Pump infinite money in and expect infinitely more money back - we'll
     * Likely get no more impressions after the recommended spend level
     */
    const recommendedSpend = calculateRecommendedSpend(actualSpend, lostImpressionShare);
    // eslint-disable-next-line no-param-reassign
    potentialSpend = Math.min(potentialSpend, recommendedSpend);

    const returnOnInvestmentPercent = getReturnOnInvestmentPercent(totalConversionValue, actualSpend);

    const potentialReturnOnInvestment = round(potentialSpend * returnOnInvestmentPercent, 2);

    return potentialReturnOnInvestment;
}

export function calculateBudgetPerDayFromSpendDuringDateRange(
    startDate: Date,
    endDate: Date,
    spend: CurrencyNumber,
): CurrencyNumber {
    const startMoment = moment(startDate).startOf('day');
    const endMoment = moment(endDate).endOf('day');
    const daysInRange = endMoment.diff(startMoment, 'day') + 1;
    const result = calculateBudgetPerDay(spend, daysInRange);
    return result;
}

export function calculateBudgetPerDay(spend: CurrencyNumber, days: number): CurrencyNumber {
    return round(spend / days, 2); // Mathematical!
}
